/**
 * Квадратики референсов «под капотом» (19.09) — ряд мини-превью в нижнем левом углу
 * плитки исходного контента инструмента.
 *
 * Зачем. Карточка с именованными фото-слотами (2+) рисует ТОЛЬКО эти слоты: ряды
 * видео/аудио-референсов в такой раскладке не появляются вовсе (UploadsRow: они в
 * ветке «обычных» загрузок). Поэтому галочка «показ» в админке для таких карточек
 * молча не работала — референс уходил в модель, а человек его не видел. Квадратик
 * закрывает ровно этот случай: канал есть, слота нет.
 *
 * Показываем только то, что админ РАЗРЕШИЛ показывать (снятый «показ» = lock прячет
 * канал), и только когда слота нет — иначе дублировали бы его.
 *
 * Содержимое квадрата — сам референс: кадр ролика, фото, у звука — волна (кадра нет).
 * Клик открывает крупный просмотр с плашкой «Референс», чтобы файл не путали с
 * результатом генерации. Только просмотр: подменить референс нельзя, он админский.
 */
import { useState } from 'react'

import CardImageLightbox from '../../../components/CardImageLightbox'

export interface ToolReferenceItem {
	kind: 'video' | 'image' | 'audio'
	/** Ссылка на ПРОСМОТР: короткая подпись с сервера либо (пока) ссылка витрины. */
	url: string
}

const LABEL: Record<ToolReferenceItem['kind'], string> = {
	video: 'Видео-референс',
	image: 'Фото-референс',
	audio: 'Аудио-референс',
}

/** Волна вместо кадра: у звука картинки нет, а квадрат должен читаться как звук. */
function AudioTile() {
	return (
		<span className="gtm__ref-wave" aria-hidden="true">
			{[5, 11, 7, 14, 9, 12, 6].map((h, i) => (
				<i key={i} style={{ height: `${h}px` }} />
			))}
		</span>
	)
}

export default function ToolReferenceThumbs({ items }: { items: ToolReferenceItem[] }) {
	const [open, setOpen] = useState<ToolReferenceItem | null>(null)
	if (!items.length) return null
	return (
		<>
			<div className="gtm__refs">
				{items.map((it) => (
					<button
						key={it.kind}
						type="button"
						className={`gtm__ref gtm__ref--${it.kind}`}
						title={`${LABEL[it.kind]} — посмотреть`}
						aria-label={`${LABEL[it.kind]} — посмотреть`}
						onClick={() => setOpen(it)}
					>
						{it.kind === 'video' ? (
							// Кадр, а не проигрывание: рядом уже крутится демо-ролик, два
							// движущихся пятна на маленькой плитке спорят друг с другом.
							<video src={it.url} muted playsInline preload="metadata" tabIndex={-1} />
						) : it.kind === 'image' ? (
							<img src={it.url} alt="" loading="lazy" decoding="async" draggable={false} />
						) : (
							<AudioTile />
						)}
					</button>
				))}
			</div>
			{open && (
				<CardImageLightbox
					images={[
						open.kind === 'video'
							? { url: '', video: open.url }
							: open.kind === 'audio'
								? { url: '', audio: open.url }
								: { url: open.url },
					]}
					badge={`Референс · ${open.kind === 'video' ? 'видео' : open.kind === 'audio' ? 'звук' : 'фото'}`}
					onClose={() => setOpen(null)}
				/>
			)}
		</>
	)
}
